import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTDocumentToSDKDocument } from '@wix/sdk-runtime/transformations/document';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateContentOptions,
  BulkCreateContentResponse,
  BulkDeleteContentResponse,
  BulkUpdateContentByKeyOptions,
  BulkUpdateContentByKeyResponse,
  BulkUpdateContentOptions,
  BulkUpdateContentRequestMaskedContent,
  BulkUpdateContentResponse,
  Content,
  ContentCreatedEnvelope,
  ContentDeletedEnvelope,
  ContentQuery,
  ContentSearch,
  ContentUpdatedEnvelope,
  ContentsQueryBuilder,
  GetContentWordCountOptions,
  GetContentWordCountResponse,
  MaskedContent,
  QueryContentsOptions,
  QueryContentsResponse,
  SearchContentsResponse,
  UpdateContent,
  UpdateContentByKeyOptions,
  UpdateContentByKeyResponse,
  UpdateContentOptions,
  bulkCreateContent as universalBulkCreateContent,
  bulkDeleteContent as universalBulkDeleteContent,
  bulkUpdateContent as universalBulkUpdateContent,
  bulkUpdateContentByKey as universalBulkUpdateContentByKey,
  createContent as universalCreateContent,
  deleteContent as universalDeleteContent,
  getContent as universalGetContent,
  getContentWordCount as universalGetContentWordCount,
  queryContents as universalQueryContents,
  searchContents as universalSearchContents,
  typedQueryContents as universalTypedQueryContents,
  updateContent as universalUpdateContent,
  updateContentByKey as universalUpdateContentByKey,
} from './multilingual-translation-v1-content-translation-contents.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/multilingual' };

export function createContent(httpClient: HttpClient): CreateContentSignature {
  return (
    content: NonNullablePaths<
      Content,
      `entityId` | `fields` | `locale` | `schemaId`,
      2
    >
  ) =>
    universalCreateContent(
      content,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateContentSignature {
  /**
   * Creates a translation content item.
   *
   * The content is validated based on the constraints defined in the corresponding schema.
   * @param - Translation content to create.
   * @returns Newly created translation content.
   */
  (
    content: NonNullablePaths<
      Content,
      `entityId` | `fields` | `locale` | `schemaId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      Content,
      `schemaId` | `entityId` | `locale` | `publishStatus`,
      2
    >
  >;
}

export function getContent(httpClient: HttpClient): GetContentSignature {
  return (contentId: string) =>
    universalGetContent(
      contentId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetContentSignature {
  /**
   * Retrieves a translation content item.
   * @param - ID of the translation content to retrieve.
   * @returns The requested translation content.
   */
  (contentId: string): Promise<
    NonNullablePaths<
      Content,
      `schemaId` | `entityId` | `locale` | `publishStatus`,
      2
    >
  >;
}

export function updateContent(httpClient: HttpClient): UpdateContentSignature {
  return (
    _id: string,
    content: NonNullablePaths<UpdateContent, `schemaId`, 2>,
    options?: UpdateContentOptions
  ) =>
    universalUpdateContent(
      _id,
      content,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateContentSignature {
  /**
   * Updates a translation content item by ID.
   *
   * To remove a field, pass the field key with an empty object as the value. For example:
   *
   * ```json
   * {
   * "fields": {
   * "title": {}
   * }
   * }
   * ```
   * @param - Translation content ID.
   * @returns Updated translation content.
   */
  (
    _id: string,
    content: NonNullablePaths<UpdateContent, `schemaId`, 2>,
    options?: UpdateContentOptions
  ): Promise<
    NonNullablePaths<
      Content,
      `schemaId` | `entityId` | `locale` | `publishStatus`,
      2
    >
  >;
}

export function updateContentByKey(
  httpClient: HttpClient
): UpdateContentByKeySignature {
  return (
    content: NonNullablePaths<Content, `entityId` | `locale` | `schemaId`, 2>,
    options?: UpdateContentByKeyOptions
  ) =>
    universalUpdateContentByKey(
      content,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateContentByKeySignature {
  /**
   * Updates a translation content item using a unique key, which is a combination of `content.schemaId`, `content.entityId`, and `content.locale`.
   *
   * To remove a field, pass the field key with an empty object as the value. For example:
   *
   * ```json
   * {
   * "fields": {
   * "title": {}
   * }
   * }
   * ```
   * @param - Content to update.
   */
  (
    content: NonNullablePaths<Content, `entityId` | `locale` | `schemaId`, 2>,
    options?: UpdateContentByKeyOptions
  ): Promise<
    NonNullablePaths<
      UpdateContentByKeyResponse,
      | `content.schemaId`
      | `content.entityId`
      | `content.locale`
      | `content.publishStatus`,
      3
    >
  >;
}

export function deleteContent(httpClient: HttpClient): DeleteContentSignature {
  return (contentId: string) =>
    universalDeleteContent(
      contentId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteContentSignature {
  /**
   * Deletes a translation content item.
   * @param - ID of the translation content to delete.
   */
  (contentId: string): Promise<void>;
}

export function queryContents(httpClient: HttpClient): QueryContentsSignature {
  return (options?: QueryContentsOptions) =>
    universalQueryContents(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryContentsSignature {
  /**
   * Creates a query to retrieve a list of translation content items.
   *
   * The `queryContents()` function builds a query to retrieve a list of translation content items and returns a `ContentsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/find) function.
   *
   * You can refine the query by chaining `ContentsQueryBuilder` functions onto the query. `ContentsQueryBuilder` functions enable you to filter, sort, and control the results that `queryContents()` returns.
   *
   * `queryContents()`  runs with the following `ContentsQueryBuilder` defaults which you can override:
   *
   * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/limit)
   * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-builder/ascending)
   *
   * The following `ContentsQueryBuilder` functions are supported for `queryContents()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/multilingual/translation/translation-content/contents-query-result/items) property in `ContentsQueryResult`.
   */
  (options?: QueryContentsOptions): ContentsQueryBuilder;
}

export function typedQueryContents(
  httpClient: HttpClient
): TypedQueryContentsSignature {
  return (query: ContentQuery, options?: QueryContentsOptions) =>
    universalTypedQueryContents(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryContentsSignature {
  /** */
  (query: ContentQuery, options?: QueryContentsOptions): Promise<
    NonNullablePaths<
      QueryContentsResponse,
      | `contents`
      | `contents.${number}.schemaId`
      | `contents.${number}.entityId`
      | `contents.${number}.locale`
      | `contents.${number}.publishStatus`,
      4
    >
  >;
}

export function searchContents(
  httpClient: HttpClient
): SearchContentsSignature {
  return (search: ContentSearch) =>
    universalSearchContents(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchContentsSignature {
  /**
   * Retrieves a list of all translation content associated with a site that matches the search query, with optional data aggregation.
   *
   * Note that this method allows you to retrieve all translation content for a site, regardless of which app created it.
   *
   * Use this method to search translation content fields on a site for a given expression, and/or to perform data aggregations on a site's translation content fields.
   *
   *
   * To learn about working with *Search* methods, see _API Query Language_ ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language))
   * and *Sorting and Paging* ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#the-sort-array) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging)).
   * @param - Search criteria including filter, sort, aggregations, and paging options.
   */
  (search: ContentSearch): Promise<
    NonNullablePaths<
      SearchContentsResponse,
      | `contents`
      | `contents.${number}.schemaId`
      | `contents.${number}.entityId`
      | `contents.${number}.locale`
      | `contents.${number}.publishStatus`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function bulkCreateContent(
  httpClient: HttpClient
): BulkCreateContentSignature {
  return (
    contents: NonNullablePaths<
      Content,
      `entityId` | `fields` | `locale` | `schemaId`,
      2
    >[],
    options?: BulkCreateContentOptions
  ) =>
    universalBulkCreateContent(
      contents,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateContentSignature {
  /**
   * Creates multiple translation content items.
   *
   * The content items are validated based on the constraints defined in the corresponding schema.
   * @param - List of translation content.
   */
  (
    contents: NonNullablePaths<
      Content,
      `entityId` | `fields` | `locale` | `schemaId`,
      2
    >[],
    options?: BulkCreateContentOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateContentResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.schemaId`
      | `results.${number}.item.entityId`
      | `results.${number}.item.locale`
      | `results.${number}.item.publishStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateContent(
  httpClient: HttpClient
): BulkUpdateContentSignature {
  return (
    contents: NonNullablePaths<
      BulkUpdateContentRequestMaskedContent,
      `content` | `content._id` | `content.schemaId`,
      3
    >[],
    options?: BulkUpdateContentOptions
  ) =>
    universalBulkUpdateContent(
      contents,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateContentSignature {
  /**
   * Updates multiple translation content items by ID.
   *
   * To remove a field, pass the field key with an empty object as the value. For example:
   *
   * ```json
   * {
   * "fields": {
   * "title": {}
   * }
   * }
   * ```
   * @param - Translation content items to update.
   */
  (
    contents: NonNullablePaths<
      BulkUpdateContentRequestMaskedContent,
      `content` | `content._id` | `content.schemaId`,
      3
    >[],
    options?: BulkUpdateContentOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateContentResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.schemaId`
      | `results.${number}.item.entityId`
      | `results.${number}.item.locale`
      | `results.${number}.item.publishStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateContentByKey(
  httpClient: HttpClient
): BulkUpdateContentByKeySignature {
  return (
    contents: NonNullablePaths<
      MaskedContent,
      `content` | `content.entityId` | `content.locale` | `content.schemaId`,
      3
    >[],
    options?: BulkUpdateContentByKeyOptions
  ) =>
    universalBulkUpdateContentByKey(
      contents,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateContentByKeySignature {
  /**
   * Updates multiple translation content items using a unique key, which is a combination of `contents.content.schemaId`, `contents.content.entityId`, and `contents.content.locale`.
   *
   * To remove a field, pass the field key with an empty object as the value. For example:
   *
   * ```json
   * {
   * "fields": {
   * "title": {}
   * }
   * }
   * ```
   * @param - Translation content items to update.
   */
  (
    contents: NonNullablePaths<
      MaskedContent,
      `content` | `content.entityId` | `content.locale` | `content.schemaId`,
      3
    >[],
    options?: BulkUpdateContentByKeyOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateContentByKeyResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.schemaId`
      | `results.${number}.item.entityId`
      | `results.${number}.item.locale`
      | `results.${number}.item.publishStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteContent(
  httpClient: HttpClient
): BulkDeleteContentSignature {
  return (contentIds: string[]) =>
    universalBulkDeleteContent(
      contentIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteContentSignature {
  /**
   * Deletes multiple translation content items.
   * @param - IDs of the translation content items to delete.
   */
  (contentIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteContentResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getContentWordCount(
  httpClient: HttpClient
): GetContentWordCountSignature {
  return (sourceLanguage: string, options?: GetContentWordCountOptions) =>
    universalGetContentWordCount(
      sourceLanguage,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetContentWordCountSignature {
  /**
   * Gets word counts and field counts for translatable content across a site.
   *
   * This method provides detailed translation analytics showing translatable source content statistics and target language translation progress, including current translations and outdated translations. Statistics are provided at the site level and broken down by translation schema for granular progress tracking.
   *
   * Call this method when you need to track translation progress or build dashboards showing translation completion rates across different translation schemas.
   * @param - Source language for the translatable content (typically a site's primary language), in IETF BCP 47 language tag format. For example, `en-US` for English as used in the U.S.
   */
  (sourceLanguage: string, options?: GetContentWordCountOptions): Promise<
    NonNullablePaths<
      GetContentWordCountResponse,
      | `sourceWordCount`
      | `totalFieldCount`
      | `schemaWordCount`
      | `schemaWordCount.${number}.schemaId`
      | `schemaWordCount.${number}.sourceWordCount`
      | `schemaWordCount.${number}.totalFieldCount`,
      4
    >
  >;
}

export const onContentCreated = EventDefinition(
  'wix.multilingual.translation.v1.content_created',
  true,
  (event: ContentCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.fields.*.updatedDate' },
            { path: 'entity.fields.*.richContent.metadata.createdTimestamp' },
            { path: 'entity.fields.*.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.fields.*.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.fields.*.video' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.fields.*.document' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.fields.*.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.fields.*.richContent.nodes.tocData.fontSize' },
            { path: 'entity.fields.*.richContent.nodes.tocData.itemSpacing' },
          ],
        },
      ]),
      ['entity.fields']
    )
)<ContentCreatedEnvelope>();
export const onContentDeleted = EventDefinition(
  'wix.multilingual.translation.v1.content_deleted',
  true,
  (event: ContentDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.fields.*.updatedDate' },
            { path: 'entity.fields.*.richContent.metadata.createdTimestamp' },
            { path: 'entity.fields.*.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.fields.*.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.fields.*.video' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.fields.*.document' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.fields.*.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.fields.*.richContent.nodes.tocData.fontSize' },
            { path: 'entity.fields.*.richContent.nodes.tocData.itemSpacing' },
          ],
        },
      ]),
      ['entity.fields']
    )
)<ContentDeletedEnvelope>();
export const onContentUpdated = EventDefinition(
  'wix.multilingual.translation.v1.content_updated',
  true,
  (event: ContentUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.fields.*.updatedDate' },
            { path: 'entity.fields.*.richContent.metadata.createdTimestamp' },
            { path: 'entity.fields.*.richContent.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.fields.*.image' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.fields.*.video' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.fields.*.document' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.fields.*.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.fields.*.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.fields.*.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.fields.*.richContent.nodes.tocData.fontSize' },
            { path: 'entity.fields.*.richContent.nodes.tocData.itemSpacing' },
          ],
        },
      ]),
      ['entity.fields']
    )
)<ContentUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  AspectRatio,
  Asset,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkActionMetadata,
  BulkContentResult,
  BulkCreateContentOptions,
  BulkCreateContentRequest,
  BulkCreateContentResponse,
  BulkDeleteContentRequest,
  BulkDeleteContentResponse,
  BulkDeleteContentResponseBulkContentResult,
  BulkUpdateContentByKeyOptions,
  BulkUpdateContentByKeyRequest,
  BulkUpdateContentByKeyResponse,
  BulkUpdateContentByKeyResponseBulkContentResult,
  BulkUpdateContentOptions,
  BulkUpdateContentRequest,
  BulkUpdateContentRequestMaskedContent,
  BulkUpdateContentResponse,
  BulkUpdateContentResponseBulkContentResult,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CellStyle,
  ChangeContentLocaleRequest,
  ChangeContentLocaleRequestQueryContextOneOf,
  ChangeContentLocaleResponse,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  Content,
  ContentCreatedEnvelope,
  ContentDeletedEnvelope,
  ContentField,
  ContentFieldValueOneOf,
  ContentQuerySpec,
  ContentSearchSpec,
  ContentUpdatedEnvelope,
  ContentsQueryBuilder,
  ContentsQueryResult,
  CreateContentRequest,
  CreateContentResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteContentRequest,
  DeleteContentResponse,
  DeleteContext,
  DeleteStatus,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventData,
  EventMetadata,
  FieldType,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetContentRequest,
  GetContentResponse,
  GetContentWordCountOptions,
  GetContentWordCountRequest,
  GetContentWordCountResponse,
  Gradient,
  GradientType,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  IncludeMissingValuesOptions,
  Indentation,
  InitialExpandedItems,
  Interval,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListStyle,
  ListValue,
  MapData,
  MapSettings,
  MapType,
  MaskedContent,
  Media,
  MentionData,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Metadata,
  MissingValues,
  Mode,
  Namespace,
  NamespaceChanged,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  OdeditorAssigned,
  OdeditorUnassigned,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  PermissiveBulkUpdateContentRequest,
  PermissiveBulkUpdateContentRequestMaskedContent,
  PermissiveBulkUpdateContentResponse,
  PermissiveBulkUpdateContentResponseBulkContentResult,
  PicassoAssigned,
  PicassoUnassigned,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  Position,
  PreviewFields,
  PricingData,
  PublishStatus,
  QueryContentsLegacyRequest,
  QueryContentsLegacyResponse,
  QueryContentsOptions,
  QueryContentsRequest,
  QueryContentsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  Rel,
  RemoveContentsByFilterRequest,
  RemoveContentsByFilterResponse,
  RepublishContentByFilterRequest,
  RepublishContentByFilterResponse,
  Resizing,
  ResponsivenessBehaviour,
  RestoreFromTrashBinByQueryRequest,
  RestoreFromTrashBinByQueryResponse,
  RestoreFromTrashBinByQueryResponseBulkContentResult,
  RestoreInfo,
  Results,
  RibbonStyles,
  RichContent,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  Scaling,
  Schema,
  SchemaField,
  SchemaKey,
  SchemaScope,
  SchemaWordCount,
  SearchContentsRequest,
  SearchContentsResponse,
  SearchDetails,
  ServiceProvisioned,
  ServiceRemoved,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  State,
  Stop,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Styles,
  StylesBorder,
  StylesPosition,
  TableCellData,
  TableData,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TocData,
  Type,
  UpdateContent,
  UpdateContentByKeyOptions,
  UpdateContentByKeyRequest,
  UpdateContentByKeyResponse,
  UpdateContentOptions,
  UpdateContentRequest,
  UpdateContentResponse,
  UpdateSchemaCacheRequest,
  UpdateSchemaCacheResponse,
  UpdaterIdentity,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  VideoResolution,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './multilingual-translation-v1-content-translation-contents.universal.js';
